import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Building2 } from 'lucide-react';

const API = 'https://api.glowytics.com/api';

// Portfolio – all accessible brands as a list, with per-platform cost buckets + GA4 revenue.
// "Bugün" (today) is fetched LIVE per brand from the platform APIs after the fast DB load.
export default function Portfolio() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { totals, brands }
  const [live, setLive] = useState({});   // { [brandId]: { status, google, meta, revenue } }
  const liveRunId = useRef(0);

  const fmt = (value) => {
    const num = Number(value) || 0;
    if (num === 0) return '₺0';
    if (Math.abs(num) >= 1_000_000) return `₺${(num / 1_000_000).toFixed(2)}M`;
    if (Math.abs(num) >= 1_000) return `₺${(num / 1_000).toFixed(1)}K`;
    return `₺${Math.round(num).toLocaleString('tr-TR')}`;
  };

  // Progressive live "today" fetch, concurrency-limited.
  const fetchLive = useCallback(async (brands) => {
    const runId = ++liveRunId.current;
    setLive(Object.fromEntries(brands.map((b) => [b.id, { status: 'pending' }])));

    const queue = [...brands];
    const worker = async () => {
      while (queue.length) {
        if (runId !== liveRunId.current) return; // superseded by a newer refresh
        const b = queue.shift();
        setLive((prev) => ({ ...prev, [b.id]: { status: 'loading' } }));
        try {
          const res = await fetch(`${API}/portfolio/live-today?brandId=${b.id}`, { credentials: 'include' });
          const j = await res.json();
          if (runId !== liveRunId.current) return;
          setLive((prev) => ({
            ...prev,
            [b.id]: { status: 'done', google: j.google, meta: j.meta, revenue: j.revenue },
          }));
        } catch {
          if (runId !== liveRunId.current) return;
          setLive((prev) => ({ ...prev, [b.id]: { status: 'error' } }));
        }
      }
    };
    await Promise.all(Array.from({ length: 4 }, worker));
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/portfolio/overview`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Sunucu hatası (${res.status})`);
      const json = await res.json();
      setData(json);
      fetchLive(json.brands || []); // fire-and-forget; fills today cells progressively
    } catch (err) {
      setError(err.message || 'Veri alınamadı');
    } finally {
      setIsLoading(false);
    }
  }, [fetchLive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals;
  const brands = data?.brands || [];

  const groups = useMemo(() => ([
    { key: 'google', label: 'Google Ads', sub: 'Harcama', accent: 'text-blue-600', head: 'bg-blue-50/60' },
    { key: 'meta', label: 'Meta', sub: 'Harcama', accent: 'text-indigo-600', head: 'bg-indigo-50/60' },
    { key: 'revenue', label: 'Revenue', sub: 'GA4 Ciro', accent: 'text-emerald-600', head: 'bg-emerald-50/60' },
  ]), []);
  const periods = [['today', 'Bugün'], ['yesterday', 'Dün'], ['month', 'Bu Ay']];

  const monthRoas = totals && (totals.google.month + totals.meta.month) > 0
    ? totals.revenue.month / (totals.google.month + totals.meta.month)
    : 0;

  // Live "today" totals across brands whose live fetch completed.
  const liveTodayTotals = useMemo(() => {
    const t = { google: 0, meta: 0, revenue: 0 };
    for (const b of brands) {
      const l = live[b.id];
      if (l?.status === 'done') {
        t.google += Number(l.google || 0);
        t.meta += Number(l.meta || 0);
        t.revenue += Number(l.revenue || 0);
      }
    }
    return t;
  }, [brands, live]);

  const liveDone = brands.filter((b) => live[b.id]?.status === 'done').length;
  const liveTotal = brands.length;
  const liveInProgress = liveTotal > 0 && liveDone < liveTotal;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-indigo-600" />
            Marka Portföyü
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Erişimin olan tüm markalar — Google &amp; Meta harcaması ve GA4 cirosu; bugün / dün / bu ay.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
            {liveInProgress && <Loader2 className="w-3 h-3 animate-spin" />}
            {liveInProgress ? `Bugün (canlı) yükleniyor… ${liveDone}/${liveTotal}` : '“Bugün” canlı API’den çekildi'}
          </span>
          <button
            onClick={fetchData}
            className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
            title="Yenile"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <SummaryCard label="Toplam Harcama (Ay)" value={fmt(totals.google.month + totals.meta.month)}
            sub={`Google ${fmt(totals.google.month)} · Meta ${fmt(totals.meta.month)}`} color="indigo" />
          <SummaryCard label="Toplam Ciro (Ay)" value={fmt(totals.revenue.month)} sub="GA4 kaynaklı" color="emerald" />
          <SummaryCard label="Portföy ROAS (Ay)" value={monthRoas > 0 ? `${monthRoas.toFixed(2)}x` : '—'} color="amber" />
          <SummaryCard label="Marka" value={`${totals.withDataCount}/${totals.brandCount}`} sub="veri olan / toplam" color="gray" />
        </div>
      )}

      {/* States */}
      {isLoading && !data && (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor…
        </div>
      )}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-4 text-sm">{error}</div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 900 }}>
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="sticky left-0 z-10 bg-white text-left px-4 py-2.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">
                    Marka
                  </th>
                  {groups.map((g) => (
                    <th key={g.key} colSpan={3} className={`text-center px-2 py-2 ${g.head} border-l border-gray-100`}>
                      <span className={`text-xs font-bold ${g.accent}`}>{g.label}</span>
                      <span className="text-[10px] text-gray-400 ml-1">{g.sub}</span>
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-gray-200">
                  <th className="sticky left-0 z-10 bg-white px-4 py-1.5"></th>
                  {groups.map((g) => (
                    periods.map(([pk, plabel]) => (
                      <th key={g.key + pk}
                        className={`px-2 py-1.5 text-right text-[11px] font-medium ${pk === 'today' ? 'text-indigo-500 bg-indigo-50/40 border-l border-gray-100' : 'text-gray-400'}`}>
                        {plabel}{pk === 'today' && <span className="ml-0.5 text-[9px] text-indigo-400">●</span>}
                      </th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* totals row */}
                {totals && (
                  <tr className="border-b border-gray-200 bg-gray-50/50 font-semibold">
                    <td className="sticky left-0 z-10 bg-gray-50/50 px-4 py-2.5 text-gray-700">Tüm Markalar</td>
                    {groups.map((g) => (
                      periods.map(([pk]) => {
                        const val = pk === 'today' ? liveTodayTotals[g.key] : totals[g.key][pk];
                        return (
                          <td key={g.key + pk}
                            className={`px-2 py-2.5 text-right tabular-nums ${pk === 'today' ? 'bg-indigo-50/40 border-l border-gray-100' : ''} ${g.key === 'revenue' ? 'text-emerald-700' : 'text-gray-800'}`}>
                            {pk === 'today' && liveInProgress ? <span className="text-gray-400">{fmt(val)}…</span> : fmt(val)}
                          </td>
                        );
                      })
                    ))}
                  </tr>
                )}
                {brands.map((b) => (
                  <BrandRow key={b.id} brand={b} groups={groups} periods={periods} fmt={fmt} live={live[b.id]} />
                ))}
                {brands.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-gray-400 py-12">Erişilebilir marka bulunamadı.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TodayCell({ live, group, fmt, isRev }) {
  const status = live?.status;
  if (!status || status === 'pending' || status === 'loading') {
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-300 inline-block" />;
  }
  if (status === 'error') {
    return <span className="text-gray-300" title="Canlı veri alınamadı">—</span>;
  }
  const val = live[group]; // may be null if brand has no connection for that platform
  if (val === null || val === undefined) return <span className="text-gray-300">—</span>;
  return <span className={val === 0 ? 'text-gray-300' : isRev ? 'text-emerald-600 font-medium' : 'text-gray-800 font-medium'}>{fmt(val)}</span>;
}

function BrandRow({ brand, groups, periods, fmt, live }) {
  const [imgOk, setImgOk] = useState(true);
  const logo = brand.logoUrl && imgOk ? brand.logoUrl : '/logoicon.png';
  return (
    <tr className={`border-b border-gray-50 hover:bg-gray-50/40 ${brand.hasData ? '' : 'opacity-60'}`}>
      <td className="sticky left-0 z-10 bg-white hover:bg-gray-50/40 px-4 py-2.5">
        <div className="flex items-center gap-2.5 min-w-[180px]">
          <img src={logo} alt={brand.name} onError={() => setImgOk(false)}
            className="w-10 h-10 object-contain bg-white border border-gray-100 rounded-sm p-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate leading-tight">{brand.name}</div>
            <div className="text-[10px] text-gray-400">{brand.currentUserRole || '—'}</div>
          </div>
        </div>
      </td>
      {groups.map((g) => (
        periods.map(([pk]) => {
          const isRev = g.key === 'revenue';
          if (pk === 'today') {
            return (
              <td key={g.key + pk} className="px-2 py-2.5 text-right tabular-nums bg-indigo-50/30 border-l border-gray-100">
                <TodayCell live={live} group={g.key} fmt={fmt} isRev={isRev} />
              </td>
            );
          }
          const val = brand[g.key][pk];
          return (
            <td key={g.key + pk}
              className={`px-2 py-2.5 text-right tabular-nums ${
                val === 0 ? 'text-gray-300' : isRev ? 'text-emerald-600 font-medium' : 'text-gray-700'
              }`}>
              {fmt(val)}
            </td>
          );
        })
      ))}
    </tr>
  );
}

function SummaryCard({ label, value, sub, color }) {
  const bar = {
    indigo: 'bg-indigo-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', gray: 'bg-gray-400',
  }[color];
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${bar}`} />
      <div className="text-xs font-medium text-gray-500 mb-1.5">{label}</div>
      <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1 truncate">{sub}</div>}
    </div>
  );
}
